import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Button, Box, AppBar, Tabs, Tab, Tooltip, Paper, IconButton } from '@mui/material';
import { 
  ZoomIn, 
  ZoomOut, 
  CenterFocusStrong, 
  Straighten, 
  Fullscreen,
  FullscreenExit,
  CropFree,
  Search,
  Close
} from '@mui/icons-material';
import { fromLonLat } from 'ol/proj';
import { toast } from 'react-toastify';

import { styled } from '@mui/system';
import SwipeableViews from 'react-swipeable-views-react-18-fix';

import AdressCity from './AdressCity';
import CoordinateSearchUtil from './CoordinateSearchUtil';
import SearchPositionUtil from './SearchPositionUtil';

import { innerColor, themeColor, modernDesignSystem } from '../constants/themes';

// Modern Renk Paleti - Uygulama Geneli İçin
const designSystem = modernDesignSystem;

const TabPanel = ({ value, index, children }) => {
  return (
    <div 
      role="tabpanel" 
      hidden={value !== index} 
      id={`tabpanel-${index}`}
      style={{ 
        height: '100%',
        overflow: 'auto'
      }}
    >
      {value === index && (
        <Box 
          p={1.5}
          sx={{ 
            height: '100%',
            overflow: 'auto',
            backgroundColor: designSystem.colors.neutral.gray50,
            '&::-webkit-scrollbar': {
              width: '4px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: designSystem.colors.neutral.gray200,
              borderRadius: '2px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: designSystem.colors.primary.light,
              borderRadius: '2px',
              '&:hover': {
                backgroundColor: designSystem.colors.primary.main,
              },
            },
          }}
        >
          {children}
        </Box>
      )}
    </div>
  );
};

const StyledTabs = styled(Tabs)(({ theme }) => ({
  background: `linear-gradient(135deg, ${designSystem.colors.secondary.main} 0%, ${designSystem.colors.tertiary.main} 100%)`,
  borderRadius: '5px 5px 0 0', // Üst köşelerde radius
  overflow: 'hidden',
  '& .MuiTabs-flexContainer': {
    justifyContent: 'center',
  },
  '& .MuiTab-root': {
    backgroundColor: 'transparent',
    color: designSystem.colors.neutral.white,
    minHeight: '50px',
    fontWeight: 600,
    fontSize: '0.85rem',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
      transform: 'translateY(-1px)',
    },
    '&.Mui-selected': {
      backgroundColor: designSystem.colors.neutral.white,
      color: designSystem.colors.secondary.dark,
      fontWeight: 700,
    }
  },
  '& .MuiTabs-indicator': {
    display: 'none',
  },
  position: 'relative !important',
  zIndex: 'auto !important',
}));

const ToggleButton = styled(IconButton)(({ open, zindex }) => ({
  background: `linear-gradient(135deg, ${designSystem.colors.primary.main} 0%, ${designSystem.colors.primary.dark} 100%)`,
  color: designSystem.colors.neutral.white,
  width: '52px',
  height: '52px',
  borderRadius: designSystem.borders.radius.medium,
  boxShadow: designSystem.shadows.elevated,
  position: 'fixed',
  top: '90px', // AppBar'dan 5px aşağıda
  left: open ? '440px' : '5px', // Panel açıkken 430px + 5px + 5px = 440px
  zIndex: zindex || 1301, // Dinamik z-index
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  border: `3px solid ${designSystem.colors.primary.surface}`,
  backdropFilter: 'blur(10px)',
  
  // Masaüstü modunda panel açıkken butonu daha şeffaf yap
  opacity: 1,
  
  '@media (min-width: 1025px)': {
    opacity: open ? 0.7 : 1, // Masaüstü modunda panel açıkken daha şeffaf
    '&:hover': {
      opacity: 1,
    }
  },
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-3px',
    left: '-3px',
    right: '-3px',
    bottom: '-3px',
    background: `linear-gradient(135deg, ${designSystem.colors.primary.light} 0%, ${designSystem.colors.secondary.main} 100%)`,
    borderRadius: designSystem.borders.radius.medium,
    zIndex: -1,
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  
  '&:hover': {
    background: `linear-gradient(135deg, ${designSystem.colors.primary.dark} 0%, ${designSystem.colors.secondary.dark} 100%)`,
    transform: 'scale(1.05) translateY(-2px)',
    boxShadow: designSystem.shadows.heavy,
    
    '&::before': {
      opacity: 1,
    }
  },
  
  '&:active': {
    transform: 'scale(0.95)',
  },

  // Panel açıkken farklı stil
  ...(open && {
    background: `linear-gradient(135deg, ${designSystem.colors.secondary.main} 0%, ${designSystem.colors.primary.main} 100%)`,
    border: `3px solid ${designSystem.colors.secondary.surface}`,
  }),

  '@media (max-width: 1024px)': {
    left: open ? 'calc(100vw - 60px)' : '5px',
  },
  '@media (max-width: 768px)': {
    left: open ? 'calc(100vw - 56px)' : '5px',
    width: '48px',
    height: '48px',
  }
}));

const AnimatedPanel = () => {
  // Ekran boyutunu algıla
  const [isTabletMode, setIsTabletMode] = useState(window.innerWidth <= 1024);
  const [open, setOpen] = useState(!isTabletMode); // Tablet modunda değilse varsayılan açık
  const [value, setValue] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [lastOpenedPanel, setLastOpenedPanel] = useState('left'); // Hangi panel en son açıldı
  
  // Redux'tan harita instance'ını al
  const { map } = useSelector((state) => state.globalMaps[0] || {});

  // Ekran boyutu değişikliklerini izle
  useEffect(() => {
    const handleResize = () => {
      const newIsTabletMode = window.innerWidth <= 1024;
      setIsTabletMode(newIsTabletMode);
      
      // Tablet modundan masaüstüne geçerken paneli aç
      if (!newIsTabletMode && isTabletMode) {
        setOpen(true);
      }
      // Masaüstünden tablet moduna geçerken paneli kapat
      else if (newIsTabletMode && !isTabletMode) {
        setOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isTabletMode]);

  const handleToggle = () => {
    const newOpenState = !open;
    setOpen(newOpenState);
    
    // Panel açılıyorsa, bu paneli en son açılan olarak işaretle
    if (newOpenState) {
      setLastOpenedPanel('left');
      localStorage.setItem('lastOpenedPanel', 'left');
      // Diğer panele bildir
      window.dispatchEvent(new CustomEvent('panelChanged', { detail: 'left' }));
    }
  };

  // Diğer panelin durumunu kontrol et
  useEffect(() => {
    const handlePanelChange = (event) => {
      setLastOpenedPanel(event.detail);
    };

    const handleStorageChange = () => {
      const storedLastPanel = localStorage.getItem('lastOpenedPanel');
      if (storedLastPanel) {
        setLastOpenedPanel(storedLastPanel);
      }
    };

    // Custom event ve localStorage değişikliklerini dinle
    window.addEventListener('panelChanged', handlePanelChange);
    window.addEventListener('storage', handleStorageChange);
    
    // Sayfa yüklendiğinde kontrol et
    handleStorageChange();

    return () => {
      window.removeEventListener('panelChanged', handlePanelChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Z-index değerini dinamik olarak hesapla
  const isTopPanel = lastOpenedPanel === 'left';
  const panelZIndex = isTopPanel ? 1300 : 1200;
  const buttonZIndex = isTopPanel ? 1301 : 1201;

  // Fullscreen durumu takibi
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleChangeIndex = (index) => {
    setValue(index);
  };

  // Harita kontrol fonksiyonları
  const handleZoomIn = () => {
    if (map) {
      const view = map.getView();
      const currentZoom = view.getZoom();
      const maxZoom = view.getMaxZoom() || 20;
      
      if (currentZoom < maxZoom) {
        // Yumuşak zoom animasyonu
        view.animate({
          zoom: currentZoom + 1,
          duration: 300, // 300ms yumuşak geçiş
          easing: (t) => t * (2 - t) // ease-out effect
        });
      } else {
        toast.info('Maksimum yakınlaştırma seviyesindesiniz', {
          position: 'top-center',
          autoClose: 2000,
          theme: 'dark',
        });
      }
    } else {
      toast.error('Harita yüklenmedi', { theme: 'dark' });
    }
  };

  const handleZoomOut = () => {
    if (map) {
      const view = map.getView();
      const currentZoom = view.getZoom();
      const minZoom = view.getMinZoom() || 0;
      
      if (currentZoom > minZoom) {
        // Yumuşak zoom animasyonu
        view.animate({
          zoom: currentZoom - 1,
          duration: 300, // 300ms yumuşak geçiş
          easing: (t) => t * (2 - t) // ease-out effect
        });
      } else {
        toast.info('Minimum yakınlaştırma seviyesindesiniz', {
          position: 'top-center',
          autoClose: 2000,
          theme: 'dark',
        });
      }
    } else {
      toast.error('Harita yüklenmedi', { theme: 'dark' });
    }
  };

  const handleZoomToExtent = () => {
    if (map) {
      try {
        const view = map.getView();
        // MapComponent'teki yorumlanmış extent değerlerini kullan
        const extent = [
          ...fromLonLat([37, 35], 'EPSG:32637'),
          ...fromLonLat([41, 39], 'EPSG:32637')
        ];
        view.fit(extent, {
          duration: 1000,
          padding: [50, 50, 50, 50]
        });
        toast.success('Tam görünüme geçildi', {
          position: 'top-center',
          autoClose: 2000,
          theme: 'dark',
        });
      } catch (error) {
        toast.error('Tam görünüme geçilemedi', { theme: 'dark' });
      }
    } else {
      toast.error('Harita yüklenmedi', { theme: 'dark' });
    }
  };

  const handleResetRotation = () => {
    if (map) {
      const view = map.getView();
      const currentRotation = view.getRotation();
      
      if (Math.abs(currentRotation) > 0.01) {
        view.animate({
          rotation: 0,
          duration: 500
        });
        toast.success('Harita düzeltildi', {
          position: 'top-center',
          autoClose: 2000,
          theme: 'dark',
        });
      } else {
        toast.info('Harita zaten düz konumda', {
          position: 'top-center',
          autoClose: 2000,
          theme: 'dark',
        });
      }
    } else {
      toast.error('Harita yüklenmedi', { theme: 'dark' });
    }
  };

  const handleFullscreen = () => {
    const mapElement = document.getElementById('map');
    if (mapElement) {
      try {
        if (!document.fullscreenElement) {
          mapElement.requestFullscreen().then(() => {
            toast.success('Tam ekran moduna geçildi', {
              position: 'top-center',
              autoClose: 2000,
              theme: 'dark',
            });
          }).catch((err) => {
            toast.error('Tam ekran moduna geçilemedi', { theme: 'dark' });
            console.error('Tam ekran hatası:', err);
          });
        } else {
          document.exitFullscreen().then(() => {
            toast.success('Tam ekran modundan çıkıldı', {
              position: 'top-center',
              autoClose: 2000,
              theme: 'dark',
            });
          }).catch((err) => {
            console.error('Tam ekran çıkış hatası:', err);
          });
        }
      } catch (error) {
        toast.error('Tam ekran işlemi desteklenmiyor', { theme: 'dark' });
      }
    } else {
      toast.error('Harita elementi bulunamadı', { theme: 'dark' });
    }
  };

  const handleCenterMap = () => {
    if (map) {
      try {
        const view = map.getView();
        // MapComponent'teki default center değerini kullan
        const defaultCenter = fromLonLat([38.8018, 37.1715], 'EPSG:32637');
        view.animate({
          center: defaultCenter,
          zoom: 9,
          duration: 1000
        });
        toast.success('Merkeze odaklandı', {
          position: 'top-center',
          autoClose: 2000,
          theme: 'dark',
        });
      } catch (error) {
        toast.error('Merkeze odaklanamadı', { theme: 'dark' });
      }
    } else {
      toast.error('Harita yüklenmedi', { theme: 'dark' });
    }
  };

  return (
    <div>
      <ToggleButton
        open={open}
        zindex={buttonZIndex}
        onClick={handleToggle}
        aria-label={open ? "Paneli Kapat" : "Arama Panelini Aç"}
      >
        {open ? <Close /> : <Search />}
      </ToggleButton>
      
      <Paper
        className="left-navigator-panel"
        elevation={0}
        sx={{
          position: 'fixed',
          top: '90px', // AppBar'dan 5px aşağıda
          left: open ? '5px' : '-430px', // Soldan 5px mesafe
          height: '53.5vh', // %50'den %53.5'e çıkarıldı (%7 artış)
          width: '430px',
          backgroundColor: designSystem.colors.neutral.white,
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          zIndex: panelZIndex, // Dinamik z-index
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '5px', // Tüm köşelerde 5px border radius
          boxShadow: open ? designSystem.shadows.elevated : 'none',
          border: `1px solid ${designSystem.colors.neutral.gray200}`,
          overflow: 'hidden', // İçeriğin border radius'a uyması için
          
          // Responsive ayarlar
          '@media (max-width: 1024px)': {
            width: '100vw',
            left: open ? '0' : '-100vw',
            borderRadius: 0,
          },
          '@media (max-width: 768px)': {
            width: '100vw',
            left: open ? '0' : '-100vw',
            borderRadius: 0,
          }
        }}
      >
        {/* Modern Header */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${designSystem.colors.primary.main} 0%, ${designSystem.colors.primary.dark} 100%)`,
            borderRadius: '5px 5px 0 0', // Üst köşelerde radius
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)', // Alta doğru gölge efekti
            zIndex: 10, // İçeriğin üstünde kalması için
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
            }
          }}
        >
          <StyledTabs
            value={value}
            onChange={handleChange}
            aria-label="Arama Seçenekleri"
            variant="fullWidth"
          >
            <Tab label="🏠 Parsel" />
            <Tab label="📍 Adres" />
            <Tab label="🧭 Koordinat" />
          </StyledTabs>
        </Box>

        <SwipeableViews 
          index={value} 
          onChangeIndex={handleChangeIndex}
          style={{ 
            flexGrow: 1,
            height: 'calc(53.5vh - 50px)', // Yeni panel yüksekliği - tab bar yüksekliği
            overflow: 'hidden'
          }}
          containerStyle={{
            height: '100%'
          }}
        >
          <TabPanel value={value} index={0}>
            <AdressCity />
          </TabPanel>
          <TabPanel value={value} index={1}>
            <SearchPositionUtil />
          </TabPanel>
          <TabPanel value={value} index={2}>
            <CoordinateSearchUtil />
          </TabPanel>
        </SwipeableViews>
      </Paper>
      {/* Modern Harita Kontrolleri */}
      <Box
        className="map-controls-container"
        sx={{
          position: 'fixed',
          bottom: '5px',
          left: '5px',
          zIndex: isFullscreen ? 9999 : buttonZIndex, // Dinamik z-index (buton ile aynı seviye)
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          
          '@media (max-width: 768px)': {
            bottom: '5px',
            left: '5px',
            gap: '6px',
          }
        }}
      >
        {[
          { icon: <ZoomIn />, action: handleZoomIn, tooltip: "Yakınlaştır", disabled: !map },
          { icon: <ZoomOut />, action: handleZoomOut, tooltip: "Uzaklaştır", disabled: !map },
          { icon: <CropFree />, action: handleZoomToExtent, tooltip: "Extent'e Yakınlaştır", disabled: !map },
          { icon: <Straighten />, action: handleResetRotation, tooltip: "Eğikliği Düzelt", disabled: !map },
          { 
            icon: isFullscreen ? <FullscreenExit /> : <Fullscreen />, 
            action: handleFullscreen, 
            tooltip: isFullscreen ? "Tam Ekrandan Çık" : "Tam Ekran",
            disabled: false,
            special: true
          },
          { icon: <CenterFocusStrong />, action: handleCenterMap, tooltip: "Merkeze Odaklan", disabled: !map }
        ].map((control, index) => (
          <Tooltip key={index} title={control.tooltip} placement="right" arrow>
            <span>
              <IconButton
                disabled={control.disabled}
                onClick={control.action}
                sx={{
                  backgroundColor: control.disabled 
                    ? designSystem.colors.neutral.gray300 
                    : control.special && isFullscreen 
                      ? designSystem.colors.secondary.main
                      : designSystem.colors.primary.main,
                  color: control.disabled 
                    ? designSystem.colors.neutral.gray500 
                    : designSystem.colors.neutral.white,
                  width: '44px',
                  height: '44px',
                  borderRadius: designSystem.borders.radius.medium,
                  border: `2px solid ${designSystem.colors.neutral.white}`,
                  boxShadow: designSystem.shadows.medium,
                  backdropFilter: 'blur(10px)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  
                  '&:hover:not(:disabled)': {
                    backgroundColor: control.special && isFullscreen 
                      ? designSystem.colors.secondary.dark
                      : designSystem.colors.primary.dark,
                    transform: 'translateY(-2px) scale(1.05)',
                    boxShadow: designSystem.shadows.elevated,
                  },
                  
                  '&:active:not(:disabled)': {
                    transform: 'translateY(0) scale(0.95)',
                  },
                  
                  '&:disabled': {
                    backgroundColor: designSystem.colors.neutral.gray300,
                    color: designSystem.colors.neutral.gray500,
                    cursor: 'not-allowed',
                  },
                  
                  // Tam ekranda özel pozisyonlama
                  ...(isFullscreen && control.special && {
                    position: 'fixed',
                    zIndex: 9999,
                  }),
                  
                  '@media (max-width: 768px)': {
                    width: '40px',
                    height: '40px',
                  }
                }}
              >
                {control.icon}
              </IconButton>
            </span>
          </Tooltip>
        ))}
      </Box>
    </div>
  );
};

export default AnimatedPanel;
